import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/boost';
import 'highcharts/modules/histogram-bellcurve';
import '../../highcharts-options';

import * as utils from '../../../utils';

import { Aeroval } from '../../../types/global';
import { TSData, TSWeeklyData } from '../../../types/data';
declare var aeroval: Aeroval;

export function plotHistogramSingleModel(data: TSData | TSWeeklyData): void {
	plotHistogram(data as TSData, 'histogram');
}

function plotHistogram(data: TSData, id: string) {
	const modelData = data[aeroval.model.dir];
	const tsChart = utils.getChartById('evaluation-ts');
	if (!tsChart || !modelData) return;

	// set time boundaries: from zoom state or from time series data
	const [xMin, xMax] = getXBoundariesFromTimeSeries();
	if (!xMin || !xMax) return;

	// window
	var frequencies = utils.getOrderedFrequencies();
	// remove yearly if single year
	/*if (frequencies.includes('yearly') && (modelData['yearly_date']?.length ?? 0) <= 1) {
		frequencies = frequencies.filter((item) => item !== 'yearly');
	}*/
	frequencies = utils.filterFrequencies(frequencies, aeroval.frequency)
	const whichFrequencies = ['primary', 'secondary'];
	const minN = Math.min(frequencies.length, whichFrequencies.length);

	// Precompute months in season before the main loop since it doesn't change
	const monthsInSeason = utils.monthsInSeason(aeroval.season);

	// Main loop optimization
	var series = [] as any[];
	for (let i = 0; i < minN; i++) {
		const freq = frequencies[i];
		const whichFreq = whichFrequencies[i] as 'primary' | 'secondary';

		// Data arrays
		const yObs = [];
		const yMod = [];

		const keyDate = `${freq}_date` as keyof TSData[keyof TSData];
		const keyObs = `${freq}_obs` as keyof TSData[keyof TSData];
		const keyMod = `${freq}_mod` as keyof TSData[keyof TSData];

		const dateArray = modelData[keyDate] as number[];
		const obsArray = modelData[keyObs] as number[];
		const modArray = modelData[keyMod] as number[];

		if (!dateArray || !obsArray || !modArray) continue;

		// Loop over dateArray once to minimize overhead
		for (let j = 0; j < dateArray.length; j++) {
			const dateValue = dateArray[j];

			// Check date range
			if (dateValue > xMin && dateValue <= xMax) {
				const obsValue = obsArray[j];
				const modValue = modArray[j];
				const currentDate = new Date(dateValue); // Create Date object once
				const iMonth = currentDate.getMonth() + 1;
				let iYear = currentDate.getFullYear();

				if (aeroval.season === 'DJF' && iMonth === 12) {
					iYear++;
				}

				if (aeroval.season === 'All' || monthsInSeason.includes(iMonth)) {
					// If single year is selected, match year
					if (!aeroval.time.includes('-') && iYear !== parseInt(aeroval.time)) {
						continue;
					}

					// Push data in bulk
					yObs.push(obsValue);
					yMod.push(modValue);
				}
			}
		}

		const colorObs = Highcharts.getOptions().colors?.[0];
		const colorMod = Highcharts.getOptions().colors?.[1];
		const modVisible = aeroval.seriesVisibility[`Model (${freq})`] ?? true;
		const obsVisible = aeroval.seriesVisibility[`Observation (${freq})`] ?? true;

		// model data
		series.push({
			type: 'histogram',
			id: `mod-${whichFreq}`,
			name: `Model (${freq})`,
			color: colorMod,
			xAxis: 0,
			yAxis: 0,
			baseSeries: `m${i}`,
			opacity: i == 0 ? 0.75 : 0.5,
			visible: modVisible
		}, {
			id: `m${i}`,
			data: yMod.filter(v => v != null),
			visible: false
		})

		// obs data
		series.push({
			type: 'histogram',
			name: `Observation (${freq})`,
			id: `obs-${whichFreq}`,
			color: colorObs,
			xAxis: 0,
			yAxis: 0,
			baseSeries: `o${i}`,
			opacity: i == 0 ? 0.75 : 0.5,
			visible: obsVisible
		}, {
			id: `o${i}`,
			data: yObs.filter(v => v != null),
			visible: false
		})
	}

	//get precomputed statistics for selected station
	var stats = {} as any;
	//if the site is a region, then take if from reg_stat data
	const keyTime = utils.getKeyTime(aeroval.time, aeroval.season);
	if (aeroval.region) {
		if (!aeroval.data.reg_stat) return;
		//get statistics
		var statistics = aeroval.data.reg_stat[aeroval.model.dir][aeroval.model.var];

		if (statistics[aeroval.region][keyTime]['nmb'] != null) {
			var nmb = statistics[aeroval.region][keyTime]['nmb'];
			stats['nmb'] = 100 * (nmb as number);
		} else {
			stats['nmb'] = null;
		}
		if (statistics[aeroval.region][keyTime]['R'] != null) {
			var r = statistics[aeroval.region][keyTime]['R'];
			stats['R'] = r;
		} else {
			stats['R'] = null;
		}
		stats['n'] = statistics[aeroval.region][keyTime]['num_valid'];
	}

	// get units
	var obsUnit = modelData.obs_unit;
	if (obsUnit != '1') {
		var xAxisUnit = `(${obsUnit})`;
	} else {
		xAxisUnit = '';
	}

	//set title
	const titleText = getTitle(data);

	Highcharts.chart({
		chart: {
			renderTo: id,
			zooming: {
				type: 'x',
			},
		},
		boost: {
			useGPUTranslations: true,
			usePreallocated: true,
		},
		title: {
			text: titleText,
		},
		subtitle: {
			text: `${aeroval.observation} - ${aeroval.model.name}`,
		},
		tooltip: {
			shared: true,
			pointFormat: '({point.x:.2f}, {point.y:.2f})',
		},
		legend: {
			enabled: false,
		},
		plotOptions: {
			series: {
				label: {
					enabled: false
				}
			},
		},
		xAxis: {
			title: {
				text: `${aeroval.parameter.name} ${xAxisUnit}`,
			},
			startOnTick: true,
			endOnTick: true,
		},
		series: series,
	}, function (chart) {
		utils.setManualExtremes(chart, ['x'])
	});
}

function getTitle(data: TSData): string {
	const modelData = data[aeroval.model.dir]
	var title = aeroval.parameter.name;
	title += (aeroval.layer != 'Surface' && aeroval.layer != 'Column') ? ` (${aeroval.layer})` : ''
	const stationName = modelData.station_display_name ? modelData.station_display_name : modelData.station_name
	// add station and time
	const regionOrStation = aeroval.station ? stationName : aeroval.region
	title += ` - ${regionOrStation}`
	// add time
	title += ` - ${aeroval.time}`;
	// add season
	title += aeroval.season != 'All' ? ` (${aeroval.season})` : ''
	return title;
}

function getXBoundariesFromTimeSeries(): [number | undefined, number | undefined] {
	const tsChart = utils.getChartById('evaluation-ts');
	var xMin: number | undefined = undefined;
	var xMax: number | undefined = undefined;

	if (tsChart) {
		xMin = (tsChart.axes[0] as any).min;
		if (aeroval.zoom?.min) {
			xMin = aeroval.zoom.min;
		}
		xMax = (tsChart.axes[0] as any).max;
		if (aeroval.zoom?.max) {
			xMax = aeroval.zoom.max;
		}
		if (aeroval.zoom && aeroval.zoom.state === false) {
			xMin = (tsChart.axes[0] as any).dataMin;
			xMax = (tsChart.axes[0] as any).dataMax;
		}
	}

	return [xMin, xMax];
}
